import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Gesta prohlížeče fotek (175) — jeden motor pro PhotoPinViewer i PhotoLightbox:
//  • pás fotek sleduje prst 1:1, po puštění dojede (nebo se vrátí) plynule,
//    rychlé švihnutí stačí i na krátkou vzdálenost; na krajích gumový odpor,
//  • dva prsty = přiblížení v místě prstů (1–4×), přiblížená fotka jde posouvat,
//  • dvojklep / dvojklik = přiblížit 2,5× v místě klepnutí, další dvojklep vrátí,
//  • tah dolů (bez přiblížení) zavře prohlížeč,
//  • Ctrl + kolečko (i gesto na touchpadu) přibližuje na počítači.
// Transformace se zapisují přímo do DOM (bez re-renderu) — proto je pohyb plynulý.
// Pás se VŽDY nastaví na konkrétní polohu; dřívější `transform = ""` po puštění
// na poslední fotce vracel pás na 1. fotku, i když počítadlo ukazovalo 2/2.

export const MAX_ZOOM = 4;
const TAP_ZOOM = 2.5;
const DOUBLE_TAP_MS = 280;
const EASE = "cubic-bezier(.22,.61,.36,1)";

const reduceMotion = () => typeof window !== "undefined" && window.matchMedia
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/**
 * stageRef  – plocha, která chytá gesta (dostane touch-action: none)
 * trackRef  – pás, který se posouvá do strany
 * index     – kolikátá fotka je na pásu vidět (u PhotoLightbox vždy 0)
 * canPrev / canNext, onStep(dir) – posun o fotku
 * mode      – "track": onStep přepne index a pás dojede sám (efekt na index),
 *             "out": pás odjede pryč a pak se zavolá onStep (fotka se vymění)
 * getZoomEl – prvek aktuální fotky, který se přibližuje
 * onTap(e)  – jednoduché klepnutí (např. špendlík); tapZoom=false vypne dvojklep
 * onClose   – tah dolů
 * resetKey  – změna (jiná fotka / otevření) vrátí zoom i pás
 */
export function usePhotoGestures({ stageRef, trackRef, index = 0, canPrev, canNext, onStep, mode = "track",
  getZoomEl, onTap, tapZoom = true, onClose, resetKey }) {
  const st = useRef({ pts: new Map(), kind: null, sx: 0, sy: 0, dx: 0, dy: 0, t0: 0, moved: false,
    pinch: null, pan: null, z: { s: 1, x: 0, y: 0 }, lastTap: null, tapTimer: null });
  const [zoomed, setZoomed] = useState(false);
  const live = useRef({});
  live.current = { index, canPrev, canNext, onStep, mode, getZoomEl, onTap, tapZoom, onClose };

  const width = () => stageRef.current?.clientWidth || window.innerWidth;
  // Okraj (178): na první / poslední fotce se místo přeskoku rozsvítí gradient
  // na kraji — fotka zůstane, jen je vidět, že dál to nejde.
  const glow = useRef({ left: null, right: null });
  const setGlow = (side, amount, animate) => {
    for (const k of ["left", "right"]) {
      const el = glow.current[k];
      if (!el) continue;
      el.style.transition = animate && !reduceMotion() ? "opacity 260ms ease-out" : "none";
      el.style.opacity = k === side ? String(Math.min(1, Math.max(0, amount))) : "0";
    }
  };
  const flashEdge = (side) => {
    setGlow(side, 0.9, false);
    setTimeout(() => setGlow(side, 0, true), 180);
  };
  const setTrack = useCallback((px, animate) => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = animate && !reduceMotion() ? `transform 320ms ${EASE}` : "none";
    el.style.transform = `translate3d(${-live.current.index * width() + px}px,0,0)`;
  }, [trackRef]); // eslint-disable-line react-hooks/exhaustive-deps
  const setDrop = (dy, animate) => {             // tah dolů = zavřít
    const el = stageRef.current;
    if (!el) return;
    el.style.transition = animate && !reduceMotion() ? `transform 240ms ${EASE}, opacity 240ms ${EASE}` : "none";
    el.style.transform = dy ? `translate3d(0,${dy}px,0)` : "";
    el.style.opacity = dy ? String(Math.max(0.35, 1 - dy / 500)) : "";
  };
  const applyZoom = (animate) => {
    const el = live.current.getZoomEl?.();
    if (!el) return;
    const { s, x, y } = st.current.z;
    el.style.transition = animate && !reduceMotion() ? `transform 220ms ${EASE}` : "none";
    el.style.transform = s === 1 && !x && !y ? "" : `translate3d(${x}px,${y}px,0) scale(${s})`;
    el.style.setProperty("--pz", String(1 / s));   // špendlíky drží stálou velikost
    el.style.willChange = s > 1 ? "transform" : "";
    setZoomed(s > 1.01);
  };
  // Posun přiblížené fotky jen v mezích — okraj fotky nesmí odjet dovnitř plochy.
  const bounds = (s) => {
    const el = live.current.getZoomEl?.();
    const stage = stageRef.current;
    if (!el || !stage) return { mx: 0, my: 0 };
    return { mx: Math.max(0, (el.offsetWidth * s - stage.clientWidth) / 2),
             my: Math.max(0, (el.offsetHeight * s - stage.clientHeight) / 2) };
  };
  const clampZ = (z, soft) => {
    const { mx, my } = bounds(z.s);
    const f = (v, m) => (soft ? (v > m ? m + (v - m) / 3 : v < -m ? -m + (v + m) / 3 : v) : clamp(v, -m, m));
    return { s: z.s, x: f(z.x, mx), y: f(z.y, my) };
  };
  // Zoom tak, aby bod pod prsty (screen) zůstal pod prsty.
  const zoomAt = (s, p, from) => {
    const el = live.current.getZoomEl?.();
    const stage = stageRef.current;
    if (!el || !stage) return from;
    const r = stage.getBoundingClientRect();
    const c = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    const qx = (p.x0 - c.x - from.x) / from.s;
    const qy = (p.y0 - c.y - from.y) / from.s;
    return { s, x: p.x - c.x - s * qx, y: p.y - c.y - s * qy };
  };
  const resetZoom = (animate) => { st.current.z = { s: 1, x: 0, y: 0 }; applyZoom(animate); };

  // Nová fotka / otevření: zoom pryč, pás na místo. Dojezd se animuje jen při
  // změně fotky v otevřeném prohlížeči, ne při otevření (resetKey).
  const prev = useRef({ index: null, resetKey: undefined });
  const boundAt = useRef(0);
  useLayoutEffect(() => {
    // Hned po otevření (pás vznikl před chvílí) se na výchozí fotku skočí bez dojezdu.
    const animate = mode === "track" && prev.current.index != null
      && prev.current.index !== index && prev.current.resetKey === resetKey
      && performance.now() - boundAt.current > 250;
    prev.current = { index, resetKey };
    trackRef.current?.querySelectorAll("[data-zoom]").forEach((el) => {
      el.style.transform = ""; el.style.transition = "none"; el.style.removeProperty("--pz");
    });
    st.current.z = { s: 1, x: 0, y: 0 };
    setZoomed(false);
    setDrop(0, false);
    setTrack(0, animate);
  }, [index, resetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Radix portál připojí obsah až po vykreslení rodiče (a rodič se kvůli tomu znovu
  // nevykreslí) — proto callback refy: pás se srovná přesně ve chvíli, kdy prvek
  // vznikne, a plocha si hned zapojí sledování šířky a Ctrl+kolečko.
  const onWheelRef = useRef(null);
  const stageSetup = useRef(null);
  const bindTrack = useCallback((el) => {
    trackRef.current = el;
    if (el) { boundAt.current = performance.now(); setTrack(0, false); }
  }, [setTrack]); // eslint-disable-line react-hooks/exhaustive-deps
  const bindStage = useCallback((el) => {
    stageSetup.current?.();
    stageSetup.current = null;
    stageRef.current = el;
    if (!el) return;
    const wheel = (e) => onWheelRef.current?.(e);
    el.addEventListener("wheel", wheel, { passive: false });  // non-passive: jinak nejde zabránit zoomu stránky
    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => { if (!st.current.pts.size) setTrack(0, false); }) : null;
    ro?.observe(el);
    stageSetup.current = () => { el.removeEventListener("wheel", wheel); ro?.disconnect(); };
    setTrack(0, false);
  }, [setTrack]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = (dir) => {
    const L = live.current;
    if (L.mode === "out") {
      setTrack(dir > 0 ? -width() : width(), true);
      setTimeout(() => { L.onStep?.(dir); }, reduceMotion() ? 0 : 200);
    } else {
      L.onStep?.(dir);
    }
  };

  const onPointerDown = (e) => {
    if (e.target.closest?.("button,input,textarea,a")) return;
    const S = st.current;
    stageRef.current?.setPointerCapture?.(e.pointerId);
    S.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (S.pts.size === 2) {
      const [a, b] = [...S.pts.values()];
      const m = mid(a, b);
      if (S.kind === "swipe" && S.moved) setTrack(0, true);
      setDrop(0, true);
      S.kind = "pinch";
      S.pinch = { d0: dist(a, b) || 1, from: { ...S.z }, x0: m.x, y0: m.y };
      S.moved = true;
      return;
    }
    if (S.pts.size > 2) return;
    S.sx = e.clientX; S.sy = e.clientY; S.dx = 0; S.dy = 0; S.t0 = performance.now(); S.moved = false;
    S.kind = S.z.s > 1.01 ? "pan" : "swipe";
    S.pan = { ...S.z };
  };

  const onPointerMove = (e) => {
    const S = st.current;
    if (!S.pts.has(e.pointerId)) return;
    S.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (S.kind === "pinch" && S.pts.size >= 2) {
      const [a, b] = [...S.pts.values()];
      const m = mid(a, b);
      const s = clamp(S.pinch.from.s * (dist(a, b) / S.pinch.d0), 0.8, MAX_ZOOM * 1.15);
      S.z = clampZ(zoomAt(s, { x0: S.pinch.x0, y0: S.pinch.y0, x: m.x, y: m.y }, S.pinch.from), true);
      applyZoom(false);
      return;
    }
    const dx = e.clientX - S.sx;
    const dy = e.clientY - S.sy;
    S.dx = dx; S.dy = dy;
    if (!S.moved && Math.hypot(dx, dy) > 8) {
      S.moved = true;
      if (S.kind === "swipe") S.axis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
    }
    if (!S.moved) return;
    if (S.kind === "pan") {
      S.z = clampZ({ s: S.z.s, x: S.pan.x + dx, y: S.pan.y + dy }, true);
      applyZoom(false);
    } else if (S.kind === "swipe") {
      const L = live.current;
      if (S.axis === "x") {
        const edge = (dx > 0 && !L.canPrev) || (dx < 0 && !L.canNext);
        setTrack(edge ? dx / 3 : dx, false);
        setGlow(dx > 0 ? "left" : "right", edge ? Math.abs(dx) / 110 : 0, false);
      } else if (L.onClose && dy > 0) {
        setDrop(dy, false);
      }
    }
  };

  const endPointer = (e, cancelled) => {
    const S = st.current;
    if (!S.pts.has(e.pointerId)) return;
    S.pts.delete(e.pointerId);
    const L = live.current;
    if (S.kind === "pinch") {
      if (S.pts.size === 1) {                      // jeden prst zůstal → pokračuje posun
        const [p] = [...S.pts.values()];
        S.sx = p.x; S.sy = p.y; S.pan = { ...S.z }; S.kind = "pan"; S.moved = true;
        return;
      }
      if (S.pts.size === 0) {
        if (S.z.s < 1.03) resetZoom(true);
        else { S.z = clampZ({ ...S.z, s: Math.min(S.z.s, MAX_ZOOM) }, false); applyZoom(true); }
        S.kind = null;
      }
      return;
    }
    if (S.pts.size > 0) return;
    const kind = S.kind; S.kind = null;
    if (kind === "pan") {
      if (S.z.s <= 1.01) { resetZoom(true); return; }
      S.z = clampZ(S.z, false); applyZoom(true);
      if (S.moved) return;
    }
    if (kind === "swipe") setGlow(null, 0, true);
    if (kind === "swipe" && S.moved && !cancelled) {
      const dt = Math.max(performance.now() - S.t0, 1);
      if (S.axis === "x") {
        const v = S.dx / dt;                        // px/ms — švihnutí
        const far = Math.abs(S.dx) > width() * 0.18 || Math.abs(v) > 0.45;
        if (far && S.dx < 0 && L.canNext) step(1);
        else if (far && S.dx > 0 && L.canPrev) step(-1);
        else setTrack(0, true);                   // vrátit na AKTUÁLNÍ fotku
      } else if (L.onClose && (S.dy > 110 || S.dy / dt > 0.5)) {
        L.onClose();
      } else {
        setDrop(0, true);
      }
      return;
    }
    if (kind === "swipe" && S.moved) { setTrack(0, true); setDrop(0, true); return; }
    if (cancelled || S.moved) return;
    // Klepnutí: dvojklep přiblíží / vrátí, jednoduché jde dál (špendlík apod.).
    const now = performance.now();
    const p = { x: e.clientX, y: e.clientY };
    if (L.tapZoom && S.lastTap && now - S.lastTap.t < DOUBLE_TAP_MS && dist(S.lastTap, p) < 30) {
      clearTimeout(S.tapTimer); S.lastTap = null;
      if (S.z.s > 1.01) resetZoom(true);
      else { S.z = clampZ(zoomAt(TAP_ZOOM, { x0: p.x, y0: p.y, x: p.x, y: p.y }, S.z), false); applyZoom(true); }
      return;
    }
    if (!L.onTap) { S.lastTap = { ...p, t: now }; return; }
    if (!L.tapZoom) { L.onTap(e); return; }
    S.lastTap = { ...p, t: now };
    const ev = { clientX: e.clientX, clientY: e.clientY, target: e.target };
    clearTimeout(S.tapTimer);
    S.tapTimer = setTimeout(() => { S.lastTap = null; live.current.onTap?.(ev); }, DOUBLE_TAP_MS);
  };

  const onWheel = (e) => {
    if (!e.ctrlKey) return;                       // Ctrl+kolečko / pinch na touchpadu
    e.preventDefault?.();
    const S = st.current;
    const s = clamp(S.z.s * Math.exp(-e.deltaY / 200), 1, MAX_ZOOM);
    S.z = s <= 1.01 ? { s: 1, x: 0, y: 0 } : clampZ(zoomAt(s, { x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY }, S.z), false);
    applyZoom(false);
  };
  onWheelRef.current = onWheel;
  useEffect(() => () => { clearTimeout(st.current.tapTimer); stageSetup.current?.(); }, []);

  return {
    zoomed,
    resetZoom: () => resetZoom(true),
    trackProps: { ref: bindTrack },
    glowRefs: { left: (el) => { glow.current.left = el; }, right: (el) => { glow.current.right = el; } },
    flashEdge,
    handlers: {
      ref: bindStage,
      onPointerDown, onPointerMove,
      onPointerUp: (e) => endPointer(e, false),
      onPointerCancel: (e) => endPointer(e, true),
      style: { touchAction: "none" },
    },
  };
}

// Gradient na okraji prohlížeče (178) — patří dovnitř „stage" (position: relative/absolute).
export function EdgeGlows({ glowRefs }) {
  if (!glowRefs) return null;
  const base = "pointer-events-none absolute inset-y-0 w-20 z-[5] opacity-0";
  return (
    <>
      <div ref={glowRefs.left} aria-hidden className={`${base} left-0`} data-testid="edge-glow-left"
        style={{ background: "linear-gradient(to right, rgba(224,112,63,.55), rgba(224,112,63,0))" }} />
      <div ref={glowRefs.right} aria-hidden className={`${base} right-0`} data-testid="edge-glow-right"
        style={{ background: "linear-gradient(to left, rgba(224,112,63,.55), rgba(224,112,63,0))" }} />
    </>
  );
}
